/**
 * An on-demand, speedtest-style measurement for developers: ping + jitter,
 * then download, then upload, each with a live reading while it runs.
 *
 * Unlike the passive monitor (./probe.js, a few hundred bytes every 10s), this
 * deliberately fills the connection for ~20s — several parallel streams, the
 * way speed tests do, because one stream cannot fill a fast link. It only ever
 * runs when someone presses Start in the developer network panel, never on its
 * own, and a live exercise will stutter while it does.
 *
 * Targets are this frontend's own static files (public/network-probe/), so the
 * backend sees none of it. Upload needs a server that accepts a POST body,
 * which static hosting cannot do: under `npm run dev` the Vite middleware in
 * vite.config.js takes it; elsewhere upload is reported as unavailable.
 */
import { median } from './quality';
import {
  assertSampleResponse,
  bust,
  createDownloadTimer,
  ping as pingOnce,
  probeRequestInit,
  SAMPLE_URL,
} from './probe';

const PING_COUNT = 10;
const PHASE_DURATION_MS = 8000;
// The first second of each transfer phase is TCP ramping up; the final figure
// is taken over what comes after it (the live reading still shows the ramp).
const PHASE_WARMUP_MS = 1000;
// Live reading = throughput over this trailing window.
const LIVE_WINDOW_MS = 1000;
const TICK_MS = 200;
const DOWNLOAD_STREAMS = 4;
const UPLOAD_STREAMS = 3;
const UPLOAD_CHUNK_BYTES = 512 * 1024;
// Hard cap per phase, whatever the link speed.
const MAX_PHASE_BYTES = 60 * 1024 * 1024;

export const SPEED_TEST_PHASE = Object.freeze({
  idle: 'idle',
  ping: 'ping',
  download: 'download',
  upload: 'upload',
  done: 'done',
  error: 'error',
});

const toMbps = (bytes, ms) => (ms > 0 ? (bytes * 8) / (ms / 1000) / 1e6 : 0);

/**
 * Byte counter shared by a phase's parallel streams, with the throughput
 * readings the UI shows.
 */
function createMeter() {
  const startedAt = performance.now();
  let total = 0;
  let warmupTotal = null;
  const marks = [{ at: startedAt, total: 0 }];

  return {
    add(bytes) {
      total += bytes;
    },
    get total() {
      return total;
    },
    elapsed() {
      return performance.now() - startedAt;
    },
    // Called every TICK_MS: records a point and returns the live reading.
    tick() {
      const now = performance.now();
      marks.push({ at: now, total });

      if (warmupTotal === null && now - startedAt >= PHASE_WARMUP_MS) {
        warmupTotal = { at: now, total };
      }

      while (marks.length > 2 && now - marks[0].at > LIVE_WINDOW_MS) {
        marks.shift();
      }

      const first = marks[0];

      return toMbps(total - first.total, now - first.at);
    },
    // Average after the warm-up; falls back to the whole phase if it was short.
    result() {
      const now = performance.now();
      const from = warmupTotal ?? { at: startedAt, total: 0 };

      return toMbps(total - from.total, now - from.at);
    },
  };
}

// Runs `stream` DOWNLOAD/UPLOAD_STREAMS times in parallel until the phase time
// or byte cap is reached, reporting the live reading every TICK_MS.
async function runPhase({ streams, stream, signal, onLive }) {
  const meter = createMeter();
  const phaseController = new AbortController();
  const stopPhase = () => phaseController.abort();
  signal.addEventListener('abort', stopPhase);

  const intervalId = window.setInterval(() => {
    onLive(meter.tick(), Math.min(1, meter.elapsed() / PHASE_DURATION_MS));

    if (meter.elapsed() >= PHASE_DURATION_MS || meter.total >= MAX_PHASE_BYTES) {
      stopPhase();
    }
  }, TICK_MS);

  const isRunning = () => !phaseController.signal.aborted;

  try {
    await Promise.all(
      Array.from({ length: streams }, () =>
        (async () => {
          while (isRunning()) {
            try {
              await stream(meter, phaseController.signal);
            } catch (error) {
              if (!isRunning()) {
                return;
              }
              // One stream failing ends the phase for all of them — otherwise
              // the others would keep transferring with nobody waiting.
              stopPhase();
              throw error;
            }
          }
        })()
      )
    );
  } finally {
    window.clearInterval(intervalId);
    signal.removeEventListener('abort', stopPhase);
  }

  if (signal.aborted) {
    throw new DOMException('Speed test stopped', 'AbortError');
  }

  return meter.result();
}

async function downloadOnce(meter, signal) {
  const response = await fetch(bust(SAMPLE_URL), probeRequestInit(signal));
  assertSampleResponse(response);

  const reader = response.body.getReader();

  for (;;) {
    const { done, value } = await reader.read();

    if (done) {
      return;
    }

    meter.add(value.byteLength);
  }
}

// XHR, not fetch: only XHR reports upload progress, which is what a live
// upload reading needs.
function uploadOnce(url, body) {
  return (meter, signal) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let reported = 0;

      const onAbort = () => xhr.abort();
      signal.addEventListener('abort', onAbort);
      const finish = callback => value => {
        signal.removeEventListener('abort', onAbort);
        callback(value);
      };

      xhr.upload.onprogress = event => {
        meter.add(event.loaded - reported);
        reported = event.loaded;
      };
      xhr.onload = finish(() =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`upload probe answered ${xhr.status}`))
      );
      xhr.onerror = finish(() => reject(new Error('upload probe failed')));
      xhr.onabort = finish(() => resolve());

      xhr.open('POST', bust(url));
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      // Same reason as probeRequestInit: past a tunnel's interstitial.
      xhr.setRequestHeader('ngrok-skip-browser-warning', '1');
      xhr.send(body);
    });
}

// Random, so no layer on the way can compress it and flatter the result.
function randomBytes(size) {
  const bytes = new Uint8Array(size);

  // getRandomValues takes at most 64 KB per call.
  for (let offset = 0; offset < size; offset += 65536) {
    crypto.getRandomValues(bytes.subarray(offset, Math.min(size, offset + 65536)));
  }

  return bytes;
}

async function measurePing(signal, onPing) {
  const ping = () => pingOnce(signal);
  const samples = [];

  // Opens the connection, so no sample pays for the handshake.
  await ping();

  for (let index = 0; index < PING_COUNT; index += 1) {
    const startedAt = performance.now();
    await ping();
    samples.push(performance.now() - startedAt);
    onPing(median(samples), (index + 1) / PING_COUNT);
  }

  const jitter =
    samples.slice(1).reduce((sum, value, index) => sum + Math.abs(value - samples[index]), 0) /
    Math.max(1, samples.length - 1);

  return { pingMs: median(samples), jitterMs: jitter };
}

/**
 * @param {object} options
 * @param {string|null} options.uploadUrl where to POST, or null when none exists
 * @param {AbortSignal} options.signal stops the test
 * @param {(update: object) => void} options.onUpdate called with every change
 */
export async function runSpeedTest({ uploadUrl, signal, onUpdate }) {
  const result = {
    phase: SPEED_TEST_PHASE.ping,
    progress: 0,
    liveMbps: null,
    pingMs: null,
    jitterMs: null,
    downloadMbps: null,
    uploadMbps: null,
    uploadUnavailable: !uploadUrl,
    error: null,
  };
  const update = patch => {
    Object.assign(result, patch);
    onUpdate({ ...result });
  };

  try {
    update({});
    const { pingMs, jitterMs } = await measurePing(signal, (live, progress) =>
      update({ pingMs: live, progress })
    );
    update({ pingMs, jitterMs });

    update({ phase: SPEED_TEST_PHASE.download, progress: 0, liveMbps: 0 });
    const downloadMbps = await runPhase({
      streams: DOWNLOAD_STREAMS,
      stream: downloadOnce,
      signal,
      onLive: (liveMbps, progress) => update({ liveMbps, progress }),
    });
    update({ downloadMbps });

    if (uploadUrl) {
      const body = randomBytes(UPLOAD_CHUNK_BYTES);
      update({ phase: SPEED_TEST_PHASE.upload, progress: 0, liveMbps: 0 });
      const uploadMbps = await runPhase({
        streams: UPLOAD_STREAMS,
        stream: uploadOnce(uploadUrl, body),
        signal,
        onLive: (liveMbps, progress) => update({ liveMbps, progress }),
      });
      update({ uploadMbps });
    }

    update({ phase: SPEED_TEST_PHASE.done, progress: 1, liveMbps: null });
  } catch (error) {
    if (error?.name === 'AbortError') {
      update({ phase: SPEED_TEST_PHASE.idle, progress: 0, liveMbps: null });
      return result;
    }

    update({
      phase: SPEED_TEST_PHASE.error,
      liveMbps: null,
      error: String(error?.message ?? error),
    });
  }

  return result;
}

// --- Live mode ---------------------------------------------------------------
// A small sample taken repeatedly while a session runs, so the panel shows
// current numbers without anyone pressing Start. Much lighter than the full
// test — two 256 KB downloads and one 256 KB upload — but at every
// LIVE_INTERVAL_MS it is still real traffic, which is why live mode is a
// switch in the developer panel that starts off.
export const LIVE_INTERVAL_MS = 10000;
const LIVE_PINGS = 3;
const LIVE_DOWNLOAD_STREAMS = 2;
const LIVE_UPLOAD_BYTES = 256 * 1024;

let liveUploadBody = null;

// Upload timed from send to response, minus one round trip. Progress events are
// no use for a body this small: most of it lands in the socket's send buffer at
// once and would read as instant.
function timeUpload(url, body, roundTripMs, signal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const onAbort = () => xhr.abort();
    signal.addEventListener('abort', onAbort);
    let sentAt = 0;

    xhr.onload = () => {
      signal.removeEventListener('abort', onAbort);

      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`upload probe answered ${xhr.status}`));
        return;
      }

      const ms = Math.max(1, performance.now() - sentAt - (roundTripMs ?? 0));
      resolve((body.byteLength * 8) / (ms / 1000) / 1e6);
    };
    xhr.onerror = () => {
      signal.removeEventListener('abort', onAbort);
      reject(new Error('upload probe failed'));
    };
    xhr.onabort = () => reject(new DOMException('Speed test stopped', 'AbortError'));

    xhr.open('POST', bust(url));
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.setRequestHeader('ngrok-skip-browser-warning', '1');
    sentAt = performance.now();
    xhr.send(body);
  });
}

/**
 * One live reading: { pingMs, jitterMs, downloadMbps, uploadMbps, error }.
 * uploadMbps is null when there is no upload target.
 */
export async function runLiveSample({ uploadUrl, signal }) {
  const reading = {
    pingMs: null,
    jitterMs: null,
    downloadMbps: null,
    uploadMbps: null,
    error: null,
  };

  try {
    await pingOnce(signal);
    const pings = [];

    for (let index = 0; index < LIVE_PINGS; index += 1) {
      const startedAt = performance.now();
      await pingOnce(signal);
      pings.push(performance.now() - startedAt);
    }

    reading.pingMs = median(pings);
    reading.jitterMs =
      pings.slice(1).reduce((sum, value, index) => sum + Math.abs(value - pings[index]), 0) /
      Math.max(1, pings.length - 1);

    const timer = createDownloadTimer();
    await Promise.all(
      Array.from({ length: LIVE_DOWNLOAD_STREAMS }, async () => {
        const response = await fetch(bust(SAMPLE_URL), probeRequestInit(signal));
        assertSampleResponse(response);
        const reader = response.body.getReader();

        for (;;) {
          const { done, value } = await reader.read();

          if (done) {
            return;
          }

          timer.add(value.byteLength);
        }
      })
    );
    reading.downloadMbps = timer.mbps();

    if (uploadUrl) {
      liveUploadBody ??= randomBytes(LIVE_UPLOAD_BYTES);
      reading.uploadMbps = await timeUpload(uploadUrl, liveUploadBody, reading.pingMs, signal);
    }
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw error;
    }

    reading.error = String(error?.message ?? error);
  }

  return reading;
}
